import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Button,
  Table,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableBody,
  TableCell,
  Text,
  Spinner,
  Badge,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { useAppContext } from '../../AppContext';
import { TaskData, TaskListItem, TENDERING_ACCESS_REQUEST_TASK_TYPE_ID } from '../../models/task';
import { Module } from '../../models/module';
import { getTasks, markTaskNotificationRead } from '../../services/taskService';
import { getUserGroupsByModuleAndOrganizationID } from '../../services/userGroupService';
import { convertToLocaleDateString } from '../../utils/date';

type RequestStatus = 'pending' | 'approved' | 'denied' | 'reapply';

const REQUEST_EXPIRED_COMMENT = 'Förfrågan har gått ut, vänligen skicka en ny förfrågan till denna grupp';

type Props = {
  open: boolean;
  onClose: () => void;
  onRequestsUpdated?: () => void;
};

const UserGroupRequests = (props: Props) => {
  const { user } = useAppContext();
  const [tasks, setTasks] = useState<TaskListItem[]>([]);
  const [moduleUserGroupsMap, setModuleUserGroupsMap] = useState<Record<number, { userGroupID: number; userGroupName: string }[]>>({});
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (props.open) {
      fetchTasks();
    }
  }, [props.open]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      // getUserTenderingAccessTasks only returns unanswered requests, so fetch from the
      // paginated task endpoint with getCompletedTasks=true to include answered ones too.
      const response = await getTasks(true, 1000, 0, TENDERING_ACCESS_REQUEST_TASK_TYPE_ID);
      // Defensive filter: this is a self-service view, only ever show the current user's own requests
      const result: TaskListItem[] = (response?.tasks ?? []).filter(
        (t: TaskListItem) => t.taskIssuedByUserID === user.user.userID
      );
      setTasks(result);
      await fetchUserGroupsForTasks(result);
      await markUnreadAnsweredRequestsAsRead(result);
    }
    catch (error) {
      console.error('Error fetching ramavtal requests:', error);
      setTasks([]);
    }
    finally {
      setLoading(false);
    }
  }

  // Now that the user is actually viewing the requests, tell the backend so the
  // "new answer" badge on the Ansökningar tab clears (persisted, not just local state).
  const markUnreadAnsweredRequestsAsRead = async (taskList: TaskListItem[]) => {
    const unreadAnsweredTasks = taskList.filter(t => {
      const answered = t.isTaskApproved === true || t.isTaskApproved === false;
      const data = getTaskData(t.taskData);
      const isRead = data.IsNotificationRead ?? data.isNotificationRead;
      return answered && !isRead;
    });

    if (unreadAnsweredTasks.length === 0) return;

    try {
      await Promise.all(unreadAnsweredTasks.map(t => markTaskNotificationRead(t.taskID)));
      props.onRequestsUpdated?.();
    }
    catch (error) {
      console.error('Error marking task notifications as read:', error);
    }
  }

  const fetchUserGroupsForTasks = async (taskList: TaskListItem[]) => {
    const uniqueModuleIDs = Array.from(new Set(taskList.map(t => t.taskModuleID)));
    if (uniqueModuleIDs.length === 0) return;

    try {
      const entries = await Promise.all(
        uniqueModuleIDs.map(async (moduleID) => {
          try {
            const groups = await getUserGroupsByModuleAndOrganizationID(moduleID, user.organization.organizationID);
            return [moduleID, groups ?? []] as const;
          }
          catch (error) {
            console.error(`Error fetching user groups for module ${moduleID}:`, error);
            return [moduleID, []] as const;
          }
        })
      );
      setModuleUserGroupsMap(Object.fromEntries(entries));
    }
    catch (error) {
      console.error('Error fetching user groups for ramavtal requests:', error);
    }
  }

  const getTaskData = (taskData: TaskData | string): TaskData => {
    try {
      return typeof taskData === 'string' ? JSON.parse(taskData) as TaskData : taskData;
    }
    catch (error) {
      console.error('Error parsing task data:', error);
      return {};
    }
  }

  const getRequestType = (task: TaskListItem): 'Join' | 'Leave' => {
    const data = getTaskData(task.taskData);
    const raw = data.RequestType ?? data.requestType;
    return raw === 1 || raw === 'Leave' ? 'Leave' : 'Join';
  }

  const getModuleName = (moduleID: number): string => {
    const module = (user.modules as Module[]).find(m => m.moduleID === moduleID);
    return module ? module.moduleName : 'Okänt ramavtal';
  }

  const getUserGroupName = (moduleID: number, userGroupID?: number): string => {
    if (!userGroupID) return '-';
    const group = moduleUserGroupsMap[moduleID]?.find(g => g.userGroupID === userGroupID);
    return group ? group.userGroupName : 'Okänd grupp';
  }

  const getRequestStatus = (task: TaskListItem): RequestStatus => {
    if (task.isTaskApproved === false) {
      if (task.taskComment === REQUEST_EXPIRED_COMMENT) return 'reapply';
      return 'denied';
    }
    if (task.isTaskApproved === null || task.isTaskApproved === undefined) return 'pending';
    return 'approved';
  }

  const renderStatusBadge = (status: RequestStatus) => {
    switch (status) {
      case 'approved':
        return <Badge color="success" appearance="filled">Godkänd</Badge>;
      case 'denied':
        return <Badge color="danger" appearance="filled">Nekad</Badge>;
      case 'reapply':
        return <Badge color="informative" appearance="filled">Ansök på nytt</Badge>;
      default:
        return <Badge color="warning" appearance="filled">Väntar på svar</Badge>;
    }
  }

  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => new Date(b.taskCreated).getTime() - new Date(a.taskCreated).getTime());
  }, [tasks]);

  return (
    <Drawer
      size="large"
      position="end"
      open={props.open}
      onOpenChange={(_event, data) => !data.open && props.onClose()}
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          <div className="flex items-start justify-between w-full">
            <h2>
              Ansökningar
            </h2>
            <Button size="small" icon={<DismissRegular />} shape="circular" onClick={props.onClose}>
            </Button>
          </div>
        </DrawerHeaderTitle>
      </DrawerHeader>
      <DrawerBody>
        <div className="flex flex-col gap-4 mt-5">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Spinner label="Laddar ansökningar..." size="large" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table aria-label="Ansökningar tabell">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell className="w-1/5"><Text weight="semibold">Ramavtal</Text></TableHeaderCell>
                    <TableHeaderCell className="w-1/5"><Text weight="semibold">Grupp</Text></TableHeaderCell>
                    <TableHeaderCell className="w-1/6"><Text weight="semibold">Typ</Text></TableHeaderCell>
                    <TableHeaderCell className="w-1/6"><Text weight="semibold">Skickad</Text></TableHeaderCell>
                    <TableHeaderCell className="w-1/6"><Text weight="semibold">Status</Text></TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTasks.length > 0 ? (
                    sortedTasks.map((task) => {
                      const data = getTaskData(task.taskData);
                      const userGroupID: number | undefined = data?.TaskUserGroupID ?? data?.taskUserGroupID;
                      const status = getRequestStatus(task);
                      const requestType = getRequestType(task);

                      return (
                        <TableRow key={task.taskID}>
                          <TableCell className="w-1/5">
                            <Text>{getModuleName(task.taskModuleID)}</Text>
                          </TableCell>
                          <TableCell className="w-1/5 break-all">
                            <Text>{getUserGroupName(task.taskModuleID, userGroupID)}</Text>
                          </TableCell>
                          <TableCell className="w-1/6">
                            {requestType === 'Leave'
                              ? <Badge color="danger" appearance="tint">Lämna grupp</Badge>
                              : <Badge color="success" appearance="tint">Gå med i grupp</Badge>}
                          </TableCell>
                          <TableCell className="w-1/6">
                            <Text>{task.taskCreated ? convertToLocaleDateString(task.taskCreated as unknown as string) : '-'}</Text>
                          </TableCell>
                          <TableCell className="w-1/6">
                            {renderStatusBadge(status)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <div className="flex justify-center py-8">
                          <Text>Du har inte skickat några ansökningar</Text>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DrawerBody>
    </Drawer>
  );
};

export default UserGroupRequests;

